import { getSanityLandingPageSlug } from '@otovo/shared/lib/getSanityLandingPageSlug';
import { StaticPropsContext } from '@otovo/shared/lib/next/types';
import { Otovo$Config } from '@otovo/shared/types/otovoweb';
import {
  Sanity$CampaignPage,
  Sanity$HomePage,
} from '@otovo/shared/types/sanityTypes';
import { useQuery } from '@sanity/react-loader';
import { InferGetStaticPropsType } from 'next';
import SanityLandingPage from '../components/SanityLandingPage/SanityLandingPage';
import { landingPageQuery } from '../components/SanityLandingPage/sanityQueries';
import StorefrontPage from '../components/StorefrontPage';
import SanityLandingPageV2 from '../components/v2/Sanity/SanityLandingPage';
import { homePageQuery } from '../components/v2/Sanity/sanityQueries';
import StorefrontPageV2 from '../components/v2/StorefrontPage';
import { getStaticProps as getStaticStorefrontProps } from '../config/getProps';
import { fetchSanityDocumentWrapper } from '../lib/sanity/fetchSanityDocumentWrapper';

type PageProps = InferGetStaticPropsType<typeof getStaticProps>;

const Home = ({ config, initial, query }: PageProps) => {
  const { data } = useQuery(query, {}, { initial });

  const storefrontV2Enabled = config.BU_CONFIG.STOREFRONT_V2_ENABLED;

  if (storefrontV2Enabled) {
    return (
      <StorefrontPageV2 config={config}>
        <SanityLandingPageV2 page={data as Sanity$HomePage} />
      </StorefrontPageV2>
    );
  }

  return (
    <StorefrontPage config={config}>
      <SanityLandingPage page={data as Sanity$CampaignPage} />
    </StorefrontPage>
  );
};

export async function getStaticProps(context: StaticPropsContext) {
  const { props, revalidate } = await getStaticStorefrontProps(context);

  const sanityProps = await fetchSanityDocumentWrapper<
    Sanity$CampaignPage | Sanity$HomePage
  >({
    config: props.config,
    context,
    sanityQuery: (config: Otovo$Config) => {
      const buConfig = config.BU_CONFIG;
      const storefrontV2Enabled = config.BU_CONFIG.STOREFRONT_V2_ENABLED;

      if (storefrontV2Enabled) {
        return homePageQuery(buConfig, context.locale);
      }

      // For the legacy storefront, we use the landing page query
      // to fetch the main landing page for the market.
      return landingPageQuery(
        'landingPage',
        getSanityLandingPageSlug(buConfig.market),
        buConfig,
        context.locale,
      );
    },

    draftMode: context.draftMode,
  });

  return {
    props: {
      ...props,
      ...sanityProps,
    },
    revalidate,
    notFound: !sanityProps.initial.data,
  };
}

export default Home;
